/**
 * <BackButton> — uses Telegram BackButton in Telegram, else a top-bar fallback.
 *
 * By default it pops the router history stack (real "previous page"). Pass an
 * explicit `onBack` only for custom behaviour, e.g. stepping back inside a
 * multi-step wizard.
 */
import { useEffect } from 'react';
import { getWebApp, isInsideTelegram } from './telegram';
import { useRouter } from './router';

interface Props {
  /** Override the default history-back behaviour (e.g. wizard step-back). */
  onBack?: () => void;
}

export function BackButton({ onBack }: Props): JSX.Element | null {
  const { back } = useRouter();
  const handler = onBack ?? back;
  const inTelegram = isInsideTelegram();

  useEffect(() => {
    if (!inTelegram) return;
    const tg = getWebApp();
    if (!tg) return;
    tg.BackButton.onClick(handler);
    tg.BackButton.show();
    return () => {
      tg.BackButton.offClick(handler);
      tg.BackButton.hide();
    };
  }, [handler, inTelegram]);

  if (inTelegram) return null;

  return (
    <button
      type="button"
      onClick={handler}
      className="mb-2 inline-flex items-center gap-1 text-sm font-medium text-tg-link"
    >
      <span aria-hidden="true">←</span>
      <span>Back</span>
    </button>
  );
}
